import { useEffect, useState, useCallback } from "react";
import api, { API, getToken } from "../api";
import { useAuth } from "../AuthContext";
import HomeView from "./HomeView";
import Phase1View from "./Phase1View";
import PhaseWorkplanView from "./PhaseWorkplanView";
import LockedView from "./LockedView";
import ActivityHistory from "./ActivityHistory";
import Versions from "./Versions";
import AthleteSites from "./AthleteSites";
import PipelineDashboard from "./PipelineDashboard";
import AnalyticsView from "./AnalyticsView";
import MarketingView from "./MarketingView";
import ClipsView from "./ClipsView";
import TasksPanel from "../components/TasksPanel";
import LeaksPanel from "../components/LeaksPanel";
import ThreadsPanel from "../components/ThreadsPanel";
import QuestionsPanel from "../components/QuestionsPanel";
import DiscussionPanel from "../components/DiscussionPanel";

// Universal sections (phase-independent) shown in the top nav.
const SECTIONS = [
  { key: "home", label: "Home" },
  { key: "tasks", label: "Tasks" },
  { key: "leaks", label: "Leak Zones" },
  { key: "threads", label: "Opportunity Threads" },
  { key: "issues", label: "Issues" },
  { key: "discussion", label: "Discussion" },
];
// Activity-feed target kind -> top-level section view.
const SECTION_FOR_KIND = {
  task: "tasks", leak: "leaks", thread: "threads",
  question: "issues", discussion: "discussion",
};

export default function MainApp() {
  const { user, logout } = useAuth();
  const [state, setState] = useState(null);
  const [view, setView] = useState("home"); // home | phase1 | locked | history | versions
  const [lockedPhase, setLockedPhase] = useState(null);
  const [savedAt, setSavedAt] = useState("");
  const [exportFallback, setExportFallback] = useState(null); // { url, json }
  const [pendingFocus, setPendingFocus] = useState(null); // {tab, focus:{kind,n,label}}
  const [dashOpen, setDashOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  // Close the dashboards dropdown on outside-click / esc
  useEffect(() => {
    if (!dashOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest?.("[data-dash-menu]")) setDashOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setDashOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [dashOpen]);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/state");
    setState(data);
    setSavedAt("Synced \u00b7 " + new Date().toLocaleTimeString());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const goHome = () => {
    setView("home");
    setPendingFocus(null);
    window.scrollTo(0, 0);
  };

  // Shared onChange for the lifted section panels (Tasks/Leaks/Threads/Issues).
  const onSectionChange = (next) => {
    setState(next);
    setSavedAt("Saved · " + new Date().toLocaleTimeString());
  };
  const enterPhase = (id) => {
    if (id === 1) setView("phase1");
    else if (id === 2) setView("phase2");
    else if (id === 3 && state.phases.find((p) => p.id === 3)?.state !== "locked") setView("phase3");
    else {
      setLockedPhase(state.phases.find((p) => p.id === id));
      setView("locked");
    }
    window.scrollTo(0, 0);
  };

  // Which phase pill is active for the current view.
  const activePhaseId =
    view === "phase1" ? 1 : view === "phase2" ? 2 : view === "phase3" ? 3 : view === "locked" ? lockedPhase?.id : null;

  // Map an activity event's target onto a view + focus, then route.
  const handleActivityJump = (ev) => {
    if (!ev || ev.type === "__history__") {
      setView("history");
      window.scrollTo(0, 0);
      return;
    }
    const t = ev.target || {};
    if (t.kind === "workstream" || t.kind === "comment") {
      setPendingFocus({ focus: t });
      setView("phase1");
      window.scrollTo(0, 0);
    } else if (t.kind === "workplan") {
      const node = (state.workplan || []).find((n) => n.id === t.id);
      setView((node?.phaseId ?? 2) === 3 ? "phase3" : "phase2");
      window.scrollTo(0, 0);
    } else if (t.kind in SECTION_FOR_KIND) {
      setPendingFocus({ focus: t });
      setView(SECTION_FOR_KIND[t.kind]);
      window.scrollTo(0, 0);
    } else if (t.kind === "milestone" || t.kind === "opportunity" || t.kind === "core") {
      // Home-page block — scroll to it after render.
      setView("home");
      setTimeout(() => {
        const el = document.querySelector(`[data-home-block="${t.kind}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("home-block-flash");
          setTimeout(() => el.classList.remove("home-block-flash"), 1600);
        }
      }, 100);
    }
  };

  const exportSnapshot = async () => {
    // The Emergent preview iframe is sandboxed and silently blocks both
    // blob-URL downloads AND top-level anchor downloads. Strategy:
    //   1. Open the backend endpoint in a NEW TAB (new tab is unsandboxed,
    //      and the endpoint returns Content-Disposition: attachment so the
    //      browser downloads immediately and the tab closes).
    //   2. If the popup is blocked, show a fallback modal with the raw JSON
    //      and a Copy-to-clipboard button so the user always has a way out.
    const token = getToken();
    const url = `${API}/export/snapshot${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const win = window.open(url, "_blank", "noopener");
    if (!win) {
      // Popup blocked. Fetch the JSON and show it in a modal.
      try {
        const { data } = await api.get("/export/snapshot");
        setExportFallback({
          url,
          json: JSON.stringify(data, null, 2),
        });
      } catch (e) {
        alert("Export failed: " + (e.response?.data?.detail || e.message));
        return;
      }
    }
    setSavedAt("Exported \u00b7 " + new Date().toLocaleTimeString());
  };

  const copyExport = async () => {
    if (!exportFallback) return;
    try {
      await navigator.clipboard.writeText(exportFallback.json);
      setSavedAt("Copied to clipboard \u00b7 " + new Date().toLocaleTimeString());
    } catch (e) {
      // Clipboard API may be blocked in sandboxed iframes. User can still
      // manually select the textarea contents.
      console.warn("Clipboard write blocked; user can copy from textarea:", e);
    }
  };

  if (!state) {
    return (
      <div className="dsl" data-testid="loading">
        <div className="wrap" style={{ paddingTop: 60, color: "var(--text-dim)" }}>
          Loading project state…
        </div>
      </div>
    );
  }

  return (
    <div className="dsl" data-testid="dsl-app">
      <header>
        <div className="wrap head-row">
          <div>
            <h1>Project New Agency</h1>
            <div className="sub">Exploration &rarr; Sprint &rarr; Scale</div>
          </div>
          <div className="mode-box">
            <button
              className="dash-trigger"
              type="button"
              onClick={() => { setView("pipeline"); window.scrollTo(0, 0); }}
              style={{ marginRight: 6 }}
              data-testid="pipeline-button"
            >
              Revenue
            </button>
            <button
              className="dash-trigger"
              type="button"
              onClick={() => { setView("athlete-sites"); window.scrollTo(0, 0); }}
              style={{ marginRight: 6 }}
              data-testid="athlete-sites-button"
            >
              Athlete Sites
            </button>
            <button
              className="dash-trigger"
              type="button"
              onClick={() => { setView("analytics"); window.scrollTo(0, 0); }}
              style={{ marginRight: 6 }}
              data-testid="analytics-button"
            >
              Analytics
            </button>
            <button
              className="dash-trigger"
              type="button"
              onClick={() => { setView("marketing"); window.scrollTo(0, 0); }}
              style={{ marginRight: 6 }}
              data-testid="marketing-button"
            >
              Marketing
            </button>
            <button
              className="dash-trigger"
              type="button"
              onClick={() => { setView("clips"); window.scrollTo(0, 0); }}
              style={{ marginRight: 6 }}
              data-testid="clips-button"
            >
              Clips
            </button>
            <div className="dash-menu" data-dash-menu data-testid="dashboards-menu">
              <button
                className="dash-trigger"
                type="button"
                onClick={() => setDashOpen((o) => !o)}
                aria-haspopup="true"
                aria-expanded={dashOpen}
                data-testid="dashboards-trigger"
              >
                Dashboards <span className={`dash-caret ${dashOpen ? "open" : ""}`}>▾</span>
              </button>
              {dashOpen && (
                <div className="dash-pop" data-testid="dashboards-popover">
                  <a
                    href="/dashboards/seo/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dash-item"
                    onClick={() => setDashOpen(false)}
                    data-testid="dashboards-link-seo"
                  >
                    <span className="dash-item-title">SEO Dash</span>
                    <span className="dash-item-sub">Keyword + AI search visibility</span>
                  </a>
                  <a
                    href="/dashboards/competitors/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dash-item"
                    onClick={() => setDashOpen(false)}
                    data-testid="dashboards-link-competitors"
                  >
                    <span className="dash-item-title">Competitors Dash</span>
                    <span className="dash-item-sub">Competitive landscape</span>
                  </a>
                  <a
                    href="/dashboards/dsl-v2/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dash-item"
                    onClick={() => setDashOpen(false)}
                    data-testid="dashboards-link-dsl-v2"
                  >
                    <span className="dash-item-title">The New Agency &mdash; DS V2</span>
                    <span className="dash-item-sub">Roster truth &amp; directory thesis</span>
                  </a>
                </div>
              )}
            </div>
            <div
              className={`who-pill ${isAdmin ? "admin" : "reviewer"}`}
              data-testid="role-pill"
            >
              {isAdmin ? "Admin · Marco" : "Review · Ward"}
            </div>
            <div className="mode-note">
              {isAdmin ? "Admin mode — edits enabled" : "Review mode — read only"}
            </div>
            <button
              className="logout-btn"
              onClick={logout}
              data-testid="logout-button"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav className="topnav" data-testid="topnav">
        <div className="wrap topnav-row">
          <div className="topnav-left">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                className={`navitem ${view === s.key ? "on" : ""}`}
                onClick={() => { setPendingFocus(null); setView(s.key); window.scrollTo(0, 0); }}
                data-testid={`nav-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="phasebar" data-testid="phasebar">
            {state.phases.map((p) => {
              const tag = p.state === "current" ? "active" : p.state === "complete" ? "complete" : "locked";
              return (
                <button
                  key={p.id}
                  className={`phasepill ${tag} ${activePhaseId === p.id ? "sel" : ""}`}
                  onClick={() => enterPhase(p.id)}
                  data-testid={`phasepill-${p.id}`}
                >
                  <span className="phasepill-n">Phase {p.id}</span>
                  <span className="phasepill-tag">{tag === "active" ? "Active" : tag === "complete" ? "Complete" : "Locked"}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      <div className="wrap">
        {view === "home" && (
          <HomeView
            state={state}
            enterPhase={enterPhase}
            isAdmin={isAdmin}
            onJump={handleActivityJump}
            onChange={(next) => {
              setState(next);
              setSavedAt("Saved \u00b7 " + new Date().toLocaleTimeString());
            }}
          />
        )}
        {view === "phase1" && (
          <Phase1View
            state={state}
            setState={setState}
            isAdmin={isAdmin}
            goHome={goHome}
            refresh={refresh}
            setSavedAt={setSavedAt}
            pendingFocus={pendingFocus}
            clearPendingFocus={() => setPendingFocus(null)}
          />
        )}
        {(view === "phase2" || view === "phase3") && (
          <PhaseWorkplanView
            key={view}
            phaseId={view === "phase3" ? 3 : 2}
            title={view === "phase3" ? "Scale What Works" : "90-Day Implementation Sprint"}
            weeks={view === "phase3" ? 13 : 12}
            state={state}
            setState={setState}
            isAdmin={isAdmin}
            goHome={goHome}
            setSavedAt={setSavedAt}
          />
        )}
        {view === "tasks" && (
          <div className="view" data-testid="section-tasks">
            <TasksPanel state={state} setState={onSectionChange} isAdmin={isAdmin} />
          </div>
        )}
        {view === "leaks" && (
          <div className="view" data-testid="section-leaks">
            <LeaksPanel state={state} setState={onSectionChange} isAdmin={isAdmin} />
          </div>
        )}
        {view === "threads" && (
          <div className="view" data-testid="section-threads">
            <ThreadsPanel state={state} setState={onSectionChange} isAdmin={isAdmin} />
          </div>
        )}
        {view === "issues" && (
          <div className="view" data-testid="section-issues">
            <QuestionsPanel state={state} setState={onSectionChange} isAdmin={isAdmin} />
          </div>
        )}
        {view === "discussion" && (
          <div className="view" data-testid="section-discussion">
            <DiscussionPanel
              isAdmin={isAdmin}
              pendingFocus={view === "discussion" ? pendingFocus : null}
              clearPendingFocus={() => setPendingFocus(null)}
            />
          </div>
        )}
        {view === "locked" && lockedPhase && (
          <LockedView phase={lockedPhase} goHome={goHome} />
        )}
        {view === "history" && (
          <ActivityHistory goHome={goHome} onJump={handleActivityJump} />
        )}
        {view === "versions" && (
          <Versions goHome={goHome} refresh={refresh} />
        )}
        {view === "athlete-sites" && (
          <AthleteSites goHome={goHome} />
        )}
        {view === "pipeline" && (
          <PipelineDashboard goHome={goHome} isAdmin={isAdmin} />
        )}
        {view === "analytics" && (
          <AnalyticsView goHome={goHome} />
        )}
        {view === "marketing" && (
          <MarketingView goHome={goHome} />
        )}
        {view === "clips" && (
          <ClipsView goHome={goHome} />
        )}

        <footer>
          <span data-testid="saved-status">{savedAt || "Connected to shared database"}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdmin && (
              <button
                onClick={() => { setView("versions"); window.scrollTo(0, 0); }}
                data-testid="versions-button"
                title="Version history · import & revert snapshots"
              >
                Versions
              </button>
            )}
            {isAdmin && (
              <button
                onClick={exportSnapshot}
                data-testid="export-snapshot-button"
                title="Download the full live project state as JSON"
              >
                Export snapshot
              </button>
            )}
            <span>Logged in as {user.email}</span>
          </span>
        </footer>
      </div>

      {exportFallback && (
        <div
          className="confirm-backdrop"
          onClick={() => setExportFallback(null)}
          data-testid="export-fallback-backdrop"
        >
          <div
            className="confirm-card"
            style={{ maxWidth: 640 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="export-fallback-dialog"
          >
            <div className="confirm-title">Snapshot ready</div>
            <div className="confirm-message">
              Your browser blocked the automatic download (this happens inside
              sandboxed previews). Use one of the options below:
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <a
                href={exportFallback.url}
                target="_blank"
                rel="noopener noreferrer"
                className="confirm-primary"
                style={{ textDecoration: "none", display: "inline-block" }}
                data-testid="export-open-tab"
              >
                Open in new tab
              </a>
              <button
                className="confirm-primary"
                onClick={copyExport}
                data-testid="export-copy-json"
              >
                Copy JSON
              </button>
            </div>
            <textarea
              readOnly
              value={exportFallback.json}
              onClick={(e) => e.target.select()}
              style={{
                width: "100%",
                minHeight: 220,
                maxHeight: "40vh",
                background: "var(--bg-input)",
                color: "var(--text)",
                border: "1px solid var(--line)",
                borderRadius: 6,
                padding: "10px 12px",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                resize: "vertical",
              }}
              data-testid="export-textarea"
            />
            <div className="confirm-actions">
              <button
                className="confirm-cancel"
                onClick={() => setExportFallback(null)}
                data-testid="export-fallback-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
